import { format, parseISO } from 'date-fns';
import { FunctionComponent, useCallback, useMemo } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  cookieOptions,
  EVENTS_FEED_ADMIN_COOKIE_KEY,
  EVENTS_FEED_COOKIE_KEY,
  FilterValue,
  GroupItem,
  isAdminLayout,
  useFilter,
} from '../../../shared';

import classes from './EventsFeedFilterBadges.module.scss';

interface RemoveBadgeArgs {
  groupId: string;
  itemId: string;
}

interface GetBadgeLabelArgs {
  groupId: string;
  item: GroupItem;
}

const dateFormat = (date: string) => format(parseISO(date), 'dd MMM yyyy');

export const EventsFeedFilterBadges: FunctionComponent = () => {
  const { t } = useTranslation();

  const { t: ctxT } = useTranslation('', { keyPrefix: 'events' });

  const cookieKey = isAdminLayout() ? EVENTS_FEED_ADMIN_COOKIE_KEY : EVENTS_FEED_COOKIE_KEY;

  const [cookies, setCookie] = useCookies([cookieKey]);

  const { getGroupValue, getDateRangeValue } = useFilter();

  const getBadgeLabel = useCallback(
    ({ groupId, item }: GetBadgeLabelArgs): string => {
      const { id: itemId, value, screenName } = item;

      switch (groupId) {
        case 'date':
          return itemId !== 'custom-range'
            ? t(`dateRangeFilter.${itemId}`)
            : `${dateFormat(value.from)} - ${dateFormat(value.to)}`;
        case 'participantStatuses':
          return ctxT(`participant-status-${itemId.toLowerCase()}`);
        case 'hostIds':
          return screenName;
        default:
          return t(`common.${itemId.toLowerCase()}`);
      }
    },
    [t, ctxT]
  );

  const badges = useMemo(() => {
    const filter: FilterValue[] = cookies[cookieKey]?.filter ?? [];

    return filter.flatMap(({ groupId, groupItems }) =>
      groupItems.map((item) => ({
        groupId,
        itemId: item.id,
        label: getBadgeLabel({ groupId, item }),
      }))
    );
  }, [cookieKey, cookies, getBadgeLabel]);

  const onRemove = useCallback(
    ({ groupId, itemId }: RemoveBadgeArgs) => {
      const filter: FilterValue[] = cookies[cookieKey]?.filter ?? [];

      const updatedFilter = filter
        .map((group) => {
          if (group.groupId !== groupId) {
            return group;
          }
          return { ...group, groupItems: group.groupItems.filter(({ id }) => id !== itemId) };
        })
        .filter(({ groupItems }) => groupItems.length > 0);

      const statuses = getGroupValue({
        filter: updatedFilter,
        groupId: 'statuses',
        multiCheck: true,
      });

      const activityStatuses = getGroupValue({
        filter: updatedFilter,
        groupId: 'activityStatuses',
        multiCheck: true,
      });

      const participantStatuses = getGroupValue({
        filter: updatedFilter,
        groupId: 'participantStatuses',
        multiCheck: true,
      });

      const { dateRange } = getDateRangeValue({ filter: updatedFilter, groupId: 'date' });

      const hostIds = getGroupValue({
        filter: updatedFilter,
        multiCheck: true,
        propToMap: 'id',
        groupId: 'hostIds',
      });

      const requestFilters = {
        ...(statuses.length && { statuses }),
        ...(activityStatuses.length && { activityStatuses }),
        ...(participantStatuses.length && { participantStatuses }),
        ...(hostIds.length && { hostIds }),
        dateRange,
      };

      setCookie(
        cookieKey,
        { ...cookies[cookieKey], requestFilters, filter: updatedFilter },
        cookieOptions()
      );
    },
    [cookieKey, cookies, getDateRangeValue, getGroupValue, setCookie]
  );

  if (!badges.length) {
    return null;
  }

  return (
    <div className={classes['filter-badges']}>
      {badges.map(({ groupId, itemId, label }) => (
        <Badge
          key={`${groupId}-${itemId}`}
          value={label}
          onRemove={() => onRemove({ groupId, itemId })}
        />
      ))}
    </div>
  );
};
